'use client';

import { createContext, useContext, useRef, useState } from 'react';
import type { KycDialogContextValue, KycDialogProviderProps, OpenKycDialogOptions } from '../types';

const KycDialogContext = createContext<KycDialogContextValue | undefined>(undefined);

function KycDialogProvider({ children }: KycDialogProviderProps) {
  const [isKycDialogOpen, setIsKycDialogOpen] = useState(false);
  const [shouldShowCloseButton, setShouldShowCloseButton] = useState(true);
  const [shouldShowBackButton, setShouldShowBackButton] = useState(false);
  const [isRegistrationFlow, setIsRegistrationFlow] = useState(false);
  const [hasOnCompleteCallback, setHasOnCompleteCallback] = useState(false);
  const [hasOnSkipVerificationCallback, setHasOnSkipVerificationCallback] = useState(false);
  const [verificationMethods, setVerificationMethods] = useState<OpenKycDialogOptions['verificationMethods']>();
  const [startMethod, setStartMethod] = useState<OpenKycDialogOptions['startMethod']>();
  const [dataTrackFeature, setDataTrackFeature] = useState<string | undefined>();
  const [dataTrackPrivacy, setDataTrackPrivacy] = useState<string | undefined>();
  const onCompleteCallbackRef = useRef<(() => void) | undefined>(undefined);
  const onSkipVerificationCallbackRef = useRef<(() => void) | undefined>(undefined);

  const isNameChangeFlow = !isRegistrationFlow && hasOnCompleteCallback && !!verificationMethods;

  const resetDialogCallbacks = () => {
    onCompleteCallbackRef.current = undefined;
    onSkipVerificationCallbackRef.current = undefined;
    setHasOnCompleteCallback(false);
    setHasOnSkipVerificationCallback(false);
    setIsRegistrationFlow(false);
    setVerificationMethods(undefined);
    setStartMethod(undefined);
    setDataTrackFeature(undefined);
    setDataTrackPrivacy(undefined);
  };

  const openKycDialog = (options?: OpenKycDialogOptions) => {
    setIsKycDialogOpen(true);
    setShouldShowCloseButton(true);
    setShouldShowBackButton(false);
    setDataTrackFeature(options?.dataTrackFeature);
    setDataTrackPrivacy(options?.dataTrackPrivacy);
    setStartMethod(options?.startMethod);

    if (options !== undefined) {
      if ('isRegistrationFlow' in options) {
        setIsRegistrationFlow(options.isRegistrationFlow ?? false);
      }
      if ('onComplete' in options) {
        onCompleteCallbackRef.current = options.onComplete;
        setHasOnCompleteCallback(!!options.onComplete);
      }
      if ('onSkipVerification' in options) {
        onSkipVerificationCallbackRef.current = options.onSkipVerification;
        setHasOnSkipVerificationCallback(!!options.onSkipVerification);
      }
      if ('verificationMethods' in options) {
        setVerificationMethods(options.verificationMethods);
      }
    }
  };

  const closeKycDialog = () => {
    setIsKycDialogOpen(false);
    setShouldShowCloseButton(true);
    setShouldShowBackButton(false);
    setIsRegistrationFlow(false);
  };

  const notifyKycCompleted = (): boolean => {
    const callback = onCompleteCallbackRef.current;
    if (!callback) return false;
    setIsKycDialogOpen(false);
    setShouldShowCloseButton(true);
    setShouldShowBackButton(false);
    resetDialogCallbacks();
    callback();
    return true;
  };

  const notifyKycSkipped = (): boolean => {
    const callback = onSkipVerificationCallbackRef.current;
    if (!callback) return false;
    setIsKycDialogOpen(false);
    setShouldShowCloseButton(true);
    setShouldShowBackButton(false);
    resetDialogCallbacks();
    callback();
    return true;
  };

  const hideCloseButton = () => setShouldShowCloseButton(false);
  const showCloseButton = () => setShouldShowCloseButton(true);
  const hideBackButton = () => setShouldShowBackButton(false);
  const showBackButtonFn = () => setShouldShowBackButton(true);

  return (
    <KycDialogContext.Provider
      value={{
        openKycDialog,
        closeKycDialog,
        notifyKycCompleted,
        notifyKycSkipped,
        isKycDialogOpen,
        isRegistrationFlow,
        hasOnCompleteCallback,
        hasOnSkipVerificationCallback,
        isNameChangeFlow,
        verificationMethods,
        startMethod,
        dataTrackFeature,
        dataTrackPrivacy,
        hideCloseButton,
        showCloseButton,
        shouldShowCloseButton,
        hideBackButton,
        showBackButton: showBackButtonFn,
        shouldShowBackButton,
      }}
    >
      {children}
    </KycDialogContext.Provider>
  );
}

function useKycDialog() {
  const context = useContext(KycDialogContext);

  if (!context) {
    throw new Error('useKycDialog must be used within KycDialogProvider');
  }

  return context;
}

/**
 * Same as `useKycDialog`, but returns `undefined` instead of throwing when there is no provider.
 * For shared components that offer the dialog as an optional extra and must still render without it.
 */
function useOptionalKycDialog() {
  return useContext(KycDialogContext);
}

export { KycDialogContext, KycDialogProvider, useKycDialog, useOptionalKycDialog };
